import {
  MindmapNodeShape,
  type ParsedMindmap,
  type MindmapTreeNode,
} from './mindmap-types';

interface ShapeParseResult {
  shape: MindmapNodeShape;
  label: string;
}

interface ParsedLineNode {
  indent: number;
  label: string;
  shape: MindmapNodeShape;
}

interface MutableMindmapNode {
  label: string;
  shape: MindmapNodeShape;
  children: MutableMindmapNode[];
}

interface StackEntry {
  indent: number;
  node: MutableMindmapNode;
}

const MINDMAP_HEADER_REGEX = /^mindmap\b/i;
const IDENTIFIER_REGEX = /^[A-Za-z0-9_][A-Za-z0-9_-]*$/;
const ID_WITH_SHAPE_REGEX = /^([A-Za-z0-9_][A-Za-z0-9_-]*)\s*(\(\([\s\S]+\)\)|\)[\s\S]+\(|\{\{[\s\S]+\}\}|\[[\s\S]+\]|\([\s\S]+\))$/;
const ID_WITH_SPACED_SHAPE_REGEX = /^([A-Za-z0-9_][A-Za-z0-9_-]*)\s+(\(\([\s\S]+\)\)|\)[\s\S]+\(|\{\{[\s\S]+\}\}|\[[\s\S]+\]|\([\s\S]+\))$/;
const ID_SHAPE_REGEXES = [
  ID_WITH_SHAPE_REGEX,
  ID_WITH_SPACED_SHAPE_REGEX,
] as const;

const SHAPE_PATTERNS: ReadonlyArray<{ shape: MindmapNodeShape; regex: RegExp }> = [
  { shape: MindmapNodeShape.Circle, regex: /^\(\(([\s\S]+)\)\)$/ },
  { shape: MindmapNodeShape.Cloud, regex: /^\)([\s\S]+)\($/ },
  { shape: MindmapNodeShape.Hexagon, regex: /^\{\{([\s\S]+)\}\}$/ },
  { shape: MindmapNodeShape.Square, regex: /^\[([\s\S]+)\]$/ },
  { shape: MindmapNodeShape.Rounded, regex: /^\(([\s\S]+)\)$/ },
];

function sanitizeMermaidLine(line: string): string {
  const commentIndex = line.indexOf('%%');
  const withoutComment = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  return withoutComment.replace(/;\s*$/, '').replace(/\s+$/, '');
}

function normalizeLabel(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

function getIndentWidth(line: string): number {
  const leadingWhitespaceMatch = line.match(/^[\t ]*/);
  const leadingWhitespace = leadingWhitespaceMatch?.[0] ?? '';

  return [...leadingWhitespace].reduce((indent, char) => {
    return indent + (char === '\t' ? 2 : 1);
  }, 0);
}

function parseShape(rawDescriptor: string): ShapeParseResult | null {
  const descriptor = rawDescriptor.trim();
  if (!descriptor) {
    return null;
  }

  for (const pattern of SHAPE_PATTERNS) {
    const match = descriptor.match(pattern.regex);
    if (!match) {
      continue;
    }

    const label = normalizeLabel(match[1] ?? '');
    if (!label) {
      return null;
    }

    return {
      shape: pattern.shape,
      label,
    };
  }

  return null;
}

function isShapeDescriptorCandidate(value: string | undefined): value is string {
  return !!value;
}

function getShapeDescriptorCandidates(trimmed: string): string[] {
  return [
    ...ID_SHAPE_REGEXES.map((regex) => trimmed.match(regex)?.[2]),
    trimmed,
  ].filter(isShapeDescriptorCandidate);
}

function parseFirstShapeDescriptor(trimmed: string): ShapeParseResult | null {
  const candidates = getShapeDescriptorCandidates(trimmed);
  return candidates
    .map((candidate) => parseShape(candidate))
    .find((parsedShape): parsedShape is ShapeParseResult => !!parsedShape) ?? null;
}

function parseTextNodeDescriptor(trimmed: string): ShapeParseResult | null {
  const label = IDENTIFIER_REGEX.test(trimmed) ? trimmed : normalizeLabel(trimmed);

  if (!label) {
    return null;
  }

  return {
    shape: MindmapNodeShape.Text,
    label,
  };
}

function parseNodeDescriptor(rawNodeLine: string): ShapeParseResult | null {
  const trimmed = rawNodeLine.trim();
  if (!trimmed) {
    return null;
  }

  const parsedShape = parseFirstShapeDescriptor(trimmed);
  if (parsedShape) {
    return parsedShape;
  }

  return parseTextNodeDescriptor(trimmed);
}

function buildTreeNode(input: {
  node: MutableMindmapNode;
  path: number[];
  depth: number;
}): MindmapTreeNode {
  const { node, path, depth } = input;

  return {
    id: `node-${path.join('-')}`,
    label: node.label,
    shape: node.shape,
    depth,
    children: node.children.map((child, childIndex) => {
      return buildTreeNode({
        node: child,
        path: [...path, childIndex],
        depth: depth + 1,
      });
    }),
  };
}

export function parseMermaidMindmap(raw: string): ParsedMindmap | null {
  const lines = raw.split('\n').map((line) => sanitizeMermaidLine(line));
  const headerIndex = lines.findIndex((line) => MINDMAP_HEADER_REGEX.test(line.trim()));
  if (headerIndex === -1) {
    return null;
  }

  const parsedLineNodes = lines
    .slice(headerIndex + 1)
    .reduce<ParsedLineNode[]>((acc, line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        return acc;
      }

      const parsedNode = parseNodeDescriptor(trimmedLine);
      if (!parsedNode) {
        return acc;
      }

      return [
        ...acc,
        {
          indent: getIndentWidth(line),
          label: parsedNode.label,
          shape: parsedNode.shape,
        },
      ];
    }, []);

  if (parsedLineNodes.length === 0) {
    return null;
  }

  const baseIndent = parsedLineNodes[0].indent;
  const stack: StackEntry[] = [];
  let root: MutableMindmapNode | null = null;

  parsedLineNodes.forEach((lineNode) => {
    const relativeIndent = Math.max(0, lineNode.indent - baseIndent);
    const nextNode: MutableMindmapNode = {
      label: lineNode.label,
      shape: lineNode.shape,
      children: [],
    };

    while (stack.length > 0 && relativeIndent <= stack[stack.length - 1].indent) {
      stack.pop();
    }

    if (!root) {
      root = nextNode;
      stack.push({ indent: relativeIndent, node: nextNode });
      return;
    }

    const parent = stack[stack.length - 1]?.node ?? root;
    parent.children.push(nextNode);
    stack.push({ indent: relativeIndent, node: nextNode });
  });

  if (!root) {
    return null;
  }

  return {
    root: buildTreeNode({
      node: root,
      path: [0],
      depth: 0,
    }),
  };
}
